import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Grid,
  Typography,
} from '@mui/material';

const EditOrder = () => {
  const { orderId } = useParams();

  const [orderItems, setOrderItems] = useState([]);
  const [availableFoods, setAvailableFoods] = useState([]);
  const [selectedFood, setSelectedFood] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [sizeQuantity, setSizeQuantity] = useState(1);
  const [customOptions, setCustomOptions] = useState([]);

  // Base sums
  const [totalCharge, setTotalCharge] = useState(0);
  const [initialTotalCharge, setInitialTotalCharge] = useState(0);

  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [servicePercent, setServicePercent] = useState(0);
  const [discountPercent, setDiscountPercent] = useState(0);

  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Computed values
  const serviceChargeAmount = (totalCharge * servicePercent) / 100;
  const discountAmount = (totalCharge * discountPercent) / 100;
  const finalTotal = totalCharge + serviceChargeAmount - discountAmount;
  const originalGrandTotal = initialTotalCharge;
  const chargeDifference = finalTotal - originalGrandTotal;

  useEffect(() => {
    const fetchOrderAndFoods = async () => {
      setLoading(true);
      try {
        // 1) Fetch existing order items
        const orderResponse = await axios.get(
          `https://kirupam.shop/admin/admin_api/order_operations.php?action=fetch&order_id=${orderId}`
        );
        const items = Array.isArray(orderResponse.data) ? orderResponse.data : [];
        setOrderItems(items);

        const existingTotal = items.reduce((sum, item) => sum + item.totalPrice, 0);
        setTotalCharge(existingTotal);
        setInitialTotalCharge(existingTotal);

        // 2) Fetch delivery charge
        const deliveryRes = await axios.get(
          `https://kirupam.shop/admin/admin_api/fetch_delivery_charge.php?order_id=${orderId}`
        );
        const fromOrder = parseFloat(deliveryRes.data.deliveryCharge || 0);
        setDeliveryCharge(fromOrder);

        // 3) If we have a storeId, fetch available foods & store details
        if (items.length > 0 && items[0].storeId) {
          const storeId = items[0].storeId;

          // 3a) Fetch available foods for this store
          const foodUrl = `https://kirupam.shop/admin/admin_api/order_operations.php?action=fetch_foods&store_id=${storeId}`;
          const foodResponse = await axios.get(foodUrl);

          // Debug: log the response to inspect its structure
          console.log("⟳ [fetch_foods] response:", foodResponse.data);

          let foodsArray;
          if (Array.isArray(foodResponse.data)) {
            foodsArray = foodResponse.data;
          } else if (
            foodResponse.data &&
            Array.isArray(foodResponse.data.data)
          ) {
            foodsArray = foodResponse.data.data;
          } else {
            foodsArray = [];
          }
          setAvailableFoods(foodsArray);

          // 3b) Fetch store’s service/offer percentages
          const storeRes = await axios.get(
            `https://kirupam.shop/admin/admin_api/fetch_store_details.php?store_id=${storeId}`
          );
          const serv = parseFloat(storeRes.data.service || 0);
          const off = parseFloat(storeRes.data.offer || 0);
          setServicePercent(serv);
          setDiscountPercent(off);
        } else {
          setSnackbarMessage('Failed to fetch store ID from order items.');
          setSnackbarOpen(true);
        }
      } catch (err) {
        console.error(err);
        setSnackbarMessage('Error fetching order, foods, or store details.');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderAndFoods();
  }, [orderId]);

  const handleSave = async () => {
    try {
      const formData = new FormData();
      formData.append('action', 'update');
      formData.append('order_id', orderId);
      formData.append('cart_items', JSON.stringify(orderItems));
      formData.append('totalCharge', finalTotal);

      const response = await axios.post(
        'https://kirupam.shop/admin/admin_api/save_changes_order.php',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.success) {
        if (Math.abs(chargeDifference) > 0) {
          setSnackbarMessage(
            `Order updated! ${chargeDifference > 0 ? 'Added' : 'Deducted'} LKR ${Math.abs(
              chargeDifference.toFixed(2)
            )} to the user's wallet.`
          );
        } else {
          setSnackbarMessage('Order updated successfully!');
        }
      } else {
        setSnackbarMessage('Failed to update order.');
      }
    } catch (err) {
      setSnackbarMessage('Error updating order.');
    } finally {
      setSnackbarOpen(true);
    }
  };

  const handleQuantityChange = (index, newQuantity) => {
    const updatedItems = [...orderItems];
    const item = updatedItems[index];

    const sizePrice = parseFloat(item.selectedSizes[0].size_price || 0);
    const customOptionsPrice = Array.isArray(item.selectedCustomOptions)
      ? item.selectedCustomOptions.reduce((sum, opt) => sum + parseFloat(opt.option_price || 0), 0)
      : 0;
    const foodPrice = parseFloat(item.basePrice || 0);

    updatedItems[index].selectedSizes[0].quantity = newQuantity;
    updatedItems[index].totalPrice = sizePrice * newQuantity + customOptionsPrice + foodPrice;

    setOrderItems(updatedItems);
    setTotalCharge(updatedItems.reduce((sum, it) => sum + it.totalPrice, 0));
  };

  const handleRemoveFood = (index) => {
    const updatedItems = [...orderItems];
    const removedPrice = updatedItems[index].totalPrice;
    updatedItems.splice(index, 1);
    setOrderItems(updatedItems);
    setTotalCharge((prev) => prev - removedPrice);
  };

  const handleAddFood = () => {
    if (!selectedFood || !selectedSize) return;

    const sizeObj = selectedFood.sizes.find((s) => s.size === selectedSize) || {};
    const sizePrice = parseFloat(sizeObj.size_price || 0);

    const optionsPrice = customOptions.reduce((sum, opt) => sum + parseFloat(opt.option_price || 0), 0);
    const basePrice = parseFloat(selectedFood.base_price || 0);

    const newItemTotal = sizePrice * sizeQuantity + optionsPrice + basePrice;

    const structuredCustom = {};
    customOptions.forEach((opt) => {
      const sz = selectedSize;
      if (!structuredCustom[sz]) structuredCustom[sz] = [];
      structuredCustom[sz].push(opt.custom_option);
    });

    const newItem = {
      foodId: selectedFood.id,
      food: selectedFood.title,
      storeId: selectedFood.storeId,
      selectedSizes: [{ size: selectedSize, quantity: sizeQuantity, size_price: sizePrice }],
      selectedCustomOptions: structuredCustom,
      totalPrice: newItemTotal,
      category: selectedFood.category,
      basePrice: selectedFood.base_price,
    };

    setOrderItems((prev) => [...prev, newItem]);
    setTotalCharge((prev) => prev + newItemTotal);

    setSelectedFood(null);
    setSelectedSize('');
    setSizeQuantity(1);
    setCustomOptions([]);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Edit Order #{orderId}
      </Typography>

      <Grid container spacing={3}>
        {/* ───── Left: Existing Order Details ───── */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Current Order Items
            </Typography>

            {orderItems.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Food</TableCell>
                      <TableCell align="center">Qty</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Options</TableCell>
                      <TableCell align="right">Item Total (LKR)</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orderItems.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.food}</TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            value={item.selectedSizes[0].quantity}
                            onChange={(e) =>
                              handleQuantityChange(idx, parseInt(e.target.value, 10) || 1)
                            }
                            size="small"
                            sx={{ width: '60px' }}
                          />
                        </TableCell>
                        <TableCell>{item.selectedSizes[0].size}</TableCell>
                        <TableCell>
                          {Object.entries(item.selectedCustomOptions).map(
                            ([sizeKey, opts], i) => (
                              <Box key={i}>
                                <Typography variant="body2">
                                  {sizeKey}: {opts.join(', ')}
                                </Typography>
                              </Box>
                            )
                          )}
                        </TableCell>
                        <TableCell align="right">{item.totalPrice.toFixed(2)}</TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={() => handleRemoveFood(idx)}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No items in this order.
              </Typography>
            )}

            {/* ───── Summary ───── */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1">
                <strong>Subtotal (Items):</strong> LKR {totalCharge.toFixed(2)}
              </Typography>
              <Typography variant="body1">
                <strong>Service Charge (%):</strong> {servicePercent.toFixed(2)}%
                {' → '}LKR {serviceChargeAmount.toFixed(2)}
              </Typography>
              <Typography variant="body1">
                <strong>Discount (%):</strong> {discountPercent.toFixed(2)}%
                {' → '}−LKR {discountAmount.toFixed(2)}
              </Typography>
              <Typography variant="body1">
                <strong>Delivery Charge:</strong> LKR {deliveryCharge.toFixed(2)}
              </Typography>
              <Typography variant="h6" sx={{ mt: 1 }}>
                <strong>New Total:</strong> LKR {finalTotal.toFixed(2)}
              </Typography>
              <Typography variant="body2" color={chargeDifference >= 0 ? 'green' : 'error'}>
                Difference from original: LKR {chargeDifference.toFixed(2)}
              </Typography>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={orderItems.length === 0}
              >
                Save Changes
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* ───── Right: Add New Food Item ───── */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Add New Food Item
            </Typography>

            <Grid container spacing={2}>
              {/* Select Food */}
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel id="food-select-label">Select Food</InputLabel>
                  <Select
                    labelId="food-select-label"
                    value={selectedFood ? selectedFood.id : ''}
                    label="Select Food"
                    onChange={(e) => {
                      const chosen = availableFoods.find((f) => f.id === e.target.value);
                      setSelectedFood(chosen);
                      setSelectedSize('');
                      setCustomOptions([]);
                    }}
                  >
                    {availableFoods.length > 0 ? (
                      availableFoods.map((food) => (
                        <MenuItem key={food.id} value={food.id}>
                          {food.title}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No foods available</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>

              {/* Select Size */}
              <Grid item xs={8}>
                <FormControl fullWidth size="small" disabled={!selectedFood}>
                  <InputLabel id="size-select-label">Select Size</InputLabel>
                  <Select
                    labelId="size-select-label"
                    value={selectedSize}
                    label="Select Size"
                    onChange={(e) => setSelectedSize(e.target.value)}
                  >
                    {selectedFood?.sizes?.map((sz) => (
                      <MenuItem key={sz.size} value={sz.size}>
                        {sz.size} – LKR {sz.size_price}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Quantity */}
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Qty"
                  type="number"
                  size="small"
                  disabled={!selectedFood}
                  inputProps={{ min: 1 }}
                  value={sizeQuantity}
                  onChange={(e) =>
                    setSizeQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))
                  }
                />
              </Grid>

              {/* Custom Options */}
              <Grid item xs={12}>
                <FormControl fullWidth size="small" disabled={!selectedSize}>
                  <InputLabel id="custom-options-label">Custom Options</InputLabel>
                  <Select
                    labelId="custom-options-label"
                    multiple
                    value={customOptions}
                    onChange={(e) => setCustomOptions(e.target.value)}
                    renderValue={(selected) => selected.map((opt) => opt.custom_option).join(', ')}
                  >
                    {selectedFood?.customOptions?.map((opt) => (
                      <MenuItem key={opt.id} value={opt}>
                        {opt.custom_option} (+LKR{opt.option_price})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Add Button */}
              <Grid item xs={12} sx={{ textAlign: 'right' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddFood}
                  disabled={!selectedFood || !selectedSize}
                >
                  Add Food
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default EditOrder;
